<template>
    <div class="task-panel-content">
        <component :is="embedded ? 'div' : 'el-dialog'" v-bind="containerProps"
            @update:visible="handleDialogVisible">
        <div class="task-panel-toolbar">
            <span class="task-panel-tip">打开面板时加载一次任务状态，后台继续处理；需要最新状态时可手动刷新。</span>
            <el-button size="small" icon="el-icon-refresh" @click="fetchTasks">刷新</el-button>
        </div>
        <el-table v-if="taskList.length > 0" :data="taskList" size="small" max-height="450" style="width:100%"
            row-key="task_id" :tree-props="{ children: 'children' }" default-expand-all>
            <el-table-column prop="task_id" label="任务ID" width="180" />
            <el-table-column v-if="mode !== 'move'" prop="local_name" label="品名" width="140" show-overflow-tooltip />
            <el-table-column v-if="mode !== 'move'" label="源FNSKU" width="135">
                <template slot-scope="scope">
                    {{ scope.row.source_fnsku || scope.row.fnsku || '-' }}
                </template>
            </el-table-column>
            <el-table-column prop="new_msku" label="MSKU" width="180" />
            <el-table-column label="操作" width="110">
                <template slot-scope="scope">
                    <el-tag v-if="scope.row.action == 'batch_create_and_procure' && !scope.row.master_task_id" type="success" size="mini">批量采购(主)</el-tag>
                    <el-tag v-else-if="scope.row.action == 'batch_create_and_procure'" type="success" size="mini" effect="plain">批量采购</el-tag>
                    <el-tag v-else-if="scope.row.action == 'create_and_procure'" type="success" size="mini">创建并采购</el-tag>
                    <el-tag v-else-if="scope.row.action == 'create_and_pair'" type="success" size="mini">创建并配对</el-tag>
                    <el-tag v-else-if="scope.row.action == 'create_only' || scope.row.action == 'create'" size="mini">创建</el-tag>
                    <el-tag v-else-if="scope.row.action == 'resell' || scope.row.action == 'create_follow'" type="success" size="mini">跟卖</el-tag>
                    <el-tag v-else-if="scope.row.action == 'move_in'" type="primary" size="mini">移入</el-tag>
                    <el-tag v-else-if="scope.row.action == 'move_out'" type="warning" size="mini">移出</el-tag>
                    <span v-else>{{ scope.row.action }}</span>
                </template>
            </el-table-column>
            <el-table-column label="操作人" width="100" align="center">
                <template slot-scope="scope">
                    <span>{{ scope.row.username || '-' }}</span>
                </template>
            </el-table-column>
            <el-table-column label="状态" width="150">
                <template slot-scope="scope">
                    <el-tag :type="taskStatusType(scope.row)" size="mini" effect="plain"
                        class="task-status-tag">
                        {{ taskStatusText(scope.row) }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="step" label="当前步骤" min-width="150" />
            <el-table-column v-if="mode !== 'move'" prop="new_asin" label="新ASIN" width="110" />
            <el-table-column v-if="mode !== 'move'" prop="new_fnsku" label="新FNSKU" width="120" />
            <el-table-column v-if="showMoveActions" fixed="right" label="操作" width="220">
                <template slot-scope="scope">
                    <el-button v-if="canMoveOutIn(scope.row)" size="small" type="danger" plain
                        :loading="scope.row._moving"
                        @click="executeMoveOutIn(scope.row)">
                        执行移入移出
                    </el-button>
                    <el-button v-if="canHighFeeMoveOut(scope.row)" size="small" type="warning" plain
                        :loading="scope.row._sourceMoving"
                        @click="openHighFeeMoveOut(scope.row)">
                        超佣移出
                    </el-button>
                    <el-tag v-else-if="scope.row.source_fnsku && scope.row.source_move_status === 'submitted'"
                        type="info" size="mini">超佣移出已提交</el-tag>
                    <el-tag v-else-if="scope.row.source_fnsku && scope.row.source_move_status === 'completed'"
                        type="success" size="mini">超佣移出已完成</el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="created_at" label="创建时间" width="170" />
        </el-table>
        <div v-else style="color:#909399;font-size:13px;padding:12px 0;">暂无任务记录</div>
    </component>

    <!-- 新建 ASIN 选择了超佣源 FNSKU 后的独立移出确认，不自动移入 -->
    <el-dialog title="超佣移出确认" :visible.sync="sourceMoveVisible" width="560px"
        :close-on-click-modal="false" append-to-body>
        <el-alert type="warning" :closable="false" show-icon
            title="这里只提交源 FNSKU 的移出任务，不会自动执行移入；请根据剩余库存自行判断。" />
        <el-descriptions v-if="sourceMoveTask" :column="2" size="small" border style="margin-top:12px;">
            <el-descriptions-item label="源FNSKU">{{ sourceMoveTask.source_fnsku || '-' }}</el-descriptions-item>
            <el-descriptions-item label="源ASIN">{{ sourceMoveTask.source_asin || sourceInventory.asin || '-' }}</el-descriptions-item>
            <el-descriptions-item label="源MSKU">{{ sourceMoveTask.source_msku || sourceInventory.msku || '-' }}</el-descriptions-item>
            <el-descriptions-item label="佣金费率">{{ formatFee(sourceMoveTask.source_fee_ratio) }}</el-descriptions-item>
            <el-descriptions-item label="剩余库存" :span="2">
                <span style="font-size:20px;font-weight:bold;color:#E6A23C;">
                    {{ sourceInventory.remaining_stock === undefined ? '-' : sourceInventory.remaining_stock }}
                </span>
                <span v-if="sourceInventory.updated_at" style="font-size:12px;color:#909399;margin-left:8px;">
                    数据时间：{{ sourceInventory.updated_at }}
                </span>
            </el-descriptions-item>
        </el-descriptions>
        <div v-if="sourceInventoryLoading" style="text-align:center;padding:18px;color:#909399;">正在查询库存…</div>
        <el-alert v-if="sourceInventoryError" type="error" :closable="false" show-icon
            :title="sourceInventoryError" style="margin-top:12px;" />
        <span slot="footer">
            <el-button @click="sourceMoveVisible = false">取消</el-button>
            <el-button type="danger" :loading="sourceMoveSubmitting"
                :disabled="!!sourceInventoryError || sourceInventoryLoading"
                @click="confirmHighFeeMoveOut">确认移出</el-button>
        </span>
        </el-dialog>
    </div>
</template>

<script>
export default {
    name: 'TaskPanelDialog',
    props: {
        visible: { type: Boolean, default: false },
        // Auto工作台内嵌时只渲染内容，不再创建第二层 el-dialog。
        embedded: { type: Boolean, default: false },
        // 内嵌模式只在当前分页激活时加载任务。
        active: { type: Boolean, default: true },
        // 'move' 只显示移入移出任务；'create' 只显示创建类任务；'all' 显示全部
        mode: { type: String, default: 'all' },
        // 是否显示「执行移入移出」操作按钮（对已完成创建发货任务）
        showMoveActions: { type: Boolean, default: false },
        // 执行移入移出所需的店铺（父组件传入）
        shop: { type: String, default: '' },
    },
    data() {
        return {
            taskList: [],
            sourceMoveVisible: false,
            sourceMoveTask: null,
            sourceInventory: {},
            sourceInventoryLoading: false,
            sourceInventoryError: '',
            sourceMoveSubmitting: false,
        };
    },
    computed: {
        containerProps() {
            if (this.embedded) return {};
            return {
                title: this.dialogTitle,
                visible: this.visible,
                width: '80%',
            };
        },
        visibleSync: {
            get() { return this.visible; },
            set(v) {
                this.$emit('update:visible', v);
            },
        },
        dialogTitle() {
            if (this.mode === 'move') return '移入移出任务';
            if (this.mode === 'create') return 'ASIN创建任务';
            return '任务面板';
        },
    },
    watch: {
        visible(v) {
            if (v && (!this.embedded || this.active)) {
                this.fetchTasks();
            }
        },
        active(v) {
            if (!this.embedded) return;
            if (v && this.visible) {
                this.fetchTasks();
            }
        },
    },
    mounted() {
        if (this.visible && (!this.embedded || this.active)) {
            this.fetchTasks();
        }
    },
    methods: {
        handleDialogVisible(value) {
            if (!this.embedded) this.visibleSync = value;
        },
        fetchTasks() {
            this.$axios.get('/vue/parent_calculate/task_list').then(res => {
                if (res.code == 200) {
                    const all = (res.data || []).map(t => {
                        const row = Object.assign({ _moving: false, children: undefined }, t);
                        // 兼容旧接口/旧记录：有些返回使用 task_status 或只返回 step，不能让状态列空白。
                        if (!row.status && row.task_status) row.status = row.task_status;
                        if (!row.status_msg) row.status_msg = this.taskStatusText(row);
                        return row;
                    });
                    let list;
                    if (this.mode === 'move') {
                        list = all.filter(t => t.action === 'move_in' || t.action === 'move_out');
                    } else if (this.mode === 'create') {
                        list = all.filter(t => t.action !== 'move_in' && t.action !== 'move_out');
                    } else {
                        list = all;
                    }
                    this.taskList = this.buildTaskTree(list);
                    this.$emit('loaded', this.taskList);
                }
            }).catch(() => { });
        },
        // 主任务（批量新建ASIN）与分支任务分组：分支任务带 master_task_id，挂到主任务下
        buildTaskTree(list) {
            const byId = {};
            list.forEach(t => { byId[t.task_id] = t; });
            const roots = [];
            list.forEach(t => {
                if (t.master_task_id && byId[t.master_task_id]) {
                    const master = byId[t.master_task_id];
                    if (!master.children) this.$set(master, 'children', []);
                    master.children.push(t);
                } else {
                    roots.push(t);
                }
            });
            return roots;
        },
        taskStatusCode(row) {
            return String((row && (row.status || row.task_status || row.state)) || '').trim().toLowerCase();
        },
        taskStatusText(row) {
            const explicit = row && (row.status_msg || row.status_text || row.status_label);
            if (explicit) return explicit;
            const status = this.taskStatusCode(row);
            const labels = {
                pending: '待开始',
                queued: '待开始',
                publishing: '刊登中',
                running: '执行中',
                processing: '执行中',
                phase1_done: '已刊登，等待FNSKU同步',
                phase2_done: 'FNSKU已同步',
                procuring: '采购中',
                complete: '任务完成',
                completed: '任务完成',
                success: '任务完成',
                failed: '任务失败',
                error: '任务失败',
            };
            if (labels[status]) return labels[status];
            if (row && row.error) return '任务失败';
            if (row && row.step) return '处理中';
            // 最后一层兜底，确保状态列始终有可读内容。
            return status || '待处理';
        },
        taskStatusType(row) {
            const status = this.taskStatusCode(row);
            if ((row && row.error) || ['failed', 'error'].includes(status)) return 'danger';
            if (['complete', 'completed', 'success'].includes(status)) return 'success';
            if (['publishing', 'running', 'processing', 'procuring'].includes(status)) return 'warning';
            return 'info';
        },
        // 是否可执行移入移出：已完成的新建发货ASIN任务（创建/创建并采购）
        canMoveOutIn(row) {
            return row.status === 'complete'
                && (row.action === 'create_only' || row.action === 'create_and_procure');
        },
        // 新建ASIN时用户显式选择了佣金费率>0.4的源FNSKU，才显示独立移出按钮。
        canHighFeeMoveOut(row) {
            const fee = Number(row.source_fee_ratio);
            return row.status === 'complete'
                && !!row.source_fnsku
                && Number.isFinite(fee) && fee > 0.4
                && row.source_move_status !== 'submitted'
                && row.source_move_status !== 'completed';
        },
        formatFee(value) {
            if (value === null || value === undefined || value === '') return '-';
            const n = Number(value);
            return Number.isFinite(n) ? n.toFixed(2) : String(value);
        },
        openHighFeeMoveOut(row) {
            this.sourceMoveTask = row;
            this.sourceInventory = {};
            this.sourceInventoryError = '';
            this.sourceMoveVisible = true;
            this.sourceInventoryLoading = true;
            this.$set(row, '_sourceMoving', true);
            this.$axios.post('/vue/parent_calculate/source_inventory', {
                task_id: row.task_id,
                shop: row.source_shop || row.procurement_shop || this.shop,
            }).then(res => {
                if (res.code === 200 && res.data) {
                    this.sourceInventory = res.data;
                    this.$set(row, 'source_asin', res.data.asin || row.source_asin || '');
                    this.$set(row, 'source_msku', res.data.msku || row.source_msku || '');
                    this.$set(row, 'source_fee_ratio', res.data.fee_ratio !== undefined
                        ? res.data.fee_ratio : row.source_fee_ratio);
                } else {
                    this.sourceInventoryError = res.msg || res.message || '库存查询失败';
                }
            }).catch(e => {
                this.sourceInventoryError = (e && e.response && e.response.data && e.response.data.msg)
                    || '库存查询请求失败';
            }).finally(() => {
                this.sourceInventoryLoading = false;
                this.$set(row, '_sourceMoving', false);
            });
        },
        confirmHighFeeMoveOut() {
            const row = this.sourceMoveTask;
            if (!row || !row.task_id) return;
            this.$confirm(
                '确认只移出源 FNSKU ' + row.source_fnsku + '？该操作不会自动移入新 ASIN。',
                '确认超佣移出',
                { confirmButtonText: '确认移出', cancelButtonText: '取消', type: 'warning' }
            ).then(() => {
                this.sourceMoveSubmitting = true;
                return this.$axios.post('/vue/parent_calculate/source_move_out', {
                    task_id: row.task_id,
                    shop: row.source_shop || row.procurement_shop || this.shop,
                });
            }).then(res => {
                if (res && res.code === 200) {
                    const result = (res.data && res.data.result) || (res.data && res.data.data) || res.data || {};
                    this.$set(row, 'source_move_status', 'submitted');
                    this.$set(row, 'source_move_task_id', result.task_id || '');
                    this.$notify.success({ title: '超佣移出已提交', message: '后台处理中，不会自动移入', duration: 5000 });
                    this.sourceMoveVisible = false;
                    this.fetchTasks();
                } else if (res) {
                    this.$message.error(res.msg || res.message || '移出失败');
                }
            }).catch(e => {
                if (e !== 'cancel' && e !== 'close') {
                    this.$message.error((e && e.message) || '移出请求失败');
                }
            }).finally(() => {
                this.sourceMoveSubmitting = false;
            });
        },
        // 执行移入移出：先移出旧ASIN，再创建跟卖产品并移入（后端 move_out_v2 + move_in_v2）
        executeMoveOutIn(row) {
            if (!this.shop) {
                this.$message.warning('缺少店铺，无法执行移入移出');
                return;
            }
            this.$confirm('确认执行移入移出操作？将先移出旧ASIN再创建跟卖产品并移入。', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.$set(row, '_moving', true);
                this.$axios.post('/vue/parent_calculate/move_out_v2', {
                    task_id: row.task_id,
                    shop: this.shop,
                }).then(res => {
                    if (res.code != 200) {
                        throw new Error(res.msg || '移出失败');
                    }
                    const moveOutTaskId = (res.data && res.data.task_id)
                        || (res.data && res.data.data && res.data.data.task_id) || '';
                    this.$notify.success({ title: '移出已提交', message: '等待移出完成后再执行移入…', duration: 3000 });
                    return this._waitMoveTask(moveOutTaskId).then(() => {
                        return this.$axios.post('/vue/parent_calculate/move_in_v2', {
                            task_id: row.task_id,
                            shop: this.shop,
                        });
                    });
                }).then(res2 => {
                    if (res2.code == 200) {
                        this.$notify.success({
                            title: '移入已提交',
                            message: '影子MSKU: ' + ((res2.data && res2.data.shadow_msku) || res2.shadow_msku || ''),
                            duration: 5000
                        });
                        const moveInTaskId = (res2.data && res2.data.task_id)
                            || (res2.data && res2.data.data && res2.data.data.task_id) || '';
                        if (moveInTaskId) this._pollMoveTask(moveInTaskId, '移入');
                    } else {
                        throw new Error(res2.msg || '移入失败');
                    }
                }).catch(e => {
                    this.$notify.error({ title: '移入移出失败', message: (e && e.message) || '请求异常', duration: 5000 });
                }).finally(() => {
                    this.$set(row, '_moving', false);
                });
            }).catch(() => { });
        },
        _waitMoveTask(taskId) {
            if (!taskId) return Promise.resolve();
            return new Promise((resolve, reject) => {
                const check = () => {
                    this.$axios.get('/vue/vue_product/task-status/' + taskId).then(res => {
                        if (res.code != 200) {
                            setTimeout(check, 5000);
                            return;
                        }
                        const task = res.data || {};
                        if (task.status === 'complete') {
                            this.$notify.success({ title: '移出完成', duration: 3000 });
                            resolve(task);
                            return;
                        }
                        if (task.status === 'failed') {
                            reject(new Error(task.error || '移出失败'));
                            return;
                        }
                        setTimeout(check, 5000);
                    }).catch(() => setTimeout(check, 5000));
                };
                check();
            });
        },
        // 轮询移入移出任务状态
        _pollMoveTask(taskId, actionName) {
            const check = () => {
                this.$axios.get('/vue/vue_product/task-status/' + taskId).then(res => {
                    if (res.code != 200) return;
                    const task = res.data;
                    if (task.status === 'complete') {
                        this.$notify.success({ title: actionName + '完成', duration: 3000 });
                        this.fetchTasks();
                        return;
                    }
                    if (task.status === 'failed') {
                        this.$notify.error({ title: actionName + '失败', message: task.error || '', duration: 5000 });
                        return;
                    }
                    setTimeout(check, 5000);
                }).catch(() => { setTimeout(check, 5000); });
            };
            setTimeout(check, 3000);
        },
    },
};
</script>

<style scoped>
.task-panel-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
    padding: 8px 10px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fafafa;
}

.task-panel-tip {
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
}

@media (max-width: 800px) {
    .task-panel-toolbar {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
